import { FC, useCallback, useState } from 'react';
import { Box, IconButton, MenuItem, Popper, PopperProps, Typography } from '@mui/material';
import { Autocomplete, AutocompleteProps } from '../inputs';
import { CoreAutocompleteChip, Option } from '../_core';
import { Colors } from '../../constants';
import { UpdateEventToFitnessTypeDto } from '@sweatpals/shared';
import { ArrowBackIcon } from '../../icons';
import {
	autocompleteOptionStyle,
	masteryLevelButton,
	masteryLevelButtonsContainer,
	masteryLevelContainerHeader,
	masteryLevelContainerStyle,
	masteryLevelIconLine,
	masteryLevelIconWrapper,
	masteryLevelLabel,
	masteryLevelSelectedActivityLabelStyle,
	masteryLevelSelectedActivityStyle,
	masteryLevelSelectLabel,
	tagLabel,
	tagLabelBox,
	tagMasteryLevelLabel,
} from './activity-autocomplete.styles';
import { uniqBy } from 'lodash-es';

export function getOldActivityValues (
	selectedActivityIds: string[],
	values?: UpdateEventToFitnessTypeDto[]
): UpdateEventToFitnessTypeDto[] {
	return (values ?? []).filter(value => !selectedActivityIds.includes(value.fitnessTypeId));
}

function findNewElement (oldArray: UpdateEventToFitnessTypeDto[], newArray: Option[]): string | null {
	const oldValues = uniqBy(oldArray ?? [], 'fitnessTypeId')
		.filter(type => !type.delete)
		.map(val => val.fitnessTypeId);
	const oldSet = new Set(oldValues);

	for (const item of newArray ?? []) {
		if (!oldSet.has(item.value)) {
			return item.value;
		}
	}

	return null;
}

function findRemovedElement (oldArray: UpdateEventToFitnessTypeDto[], newArray: Option[]): string | null {
	const oldValues = uniqBy(oldArray ?? [], 'fitnessTypeId')
		.filter(type => !type.delete)
		.map(val => val.fitnessTypeId);
	const newSet = new Set((newArray ?? []).map(opt => opt.value));

	for (const item of oldValues) {
		if (!newSet.has(item)) {
			return item;
		}
	}

	return null;
}

export type ActivityAutocompleteProps = Omit<
	AutocompleteProps,
	| 'value'
	| 'options'
	| 'onChange'
	| 'overrideRenderOption'
	| 'PopperComponent'
	| 'renderTags'
	| 'multiple'
	| 'disableCloseOnSelect'
> & {
	value?: UpdateEventToFitnessTypeDto[];
	options: Option[];
	allowSelectMasteryLevel?: boolean;
	onChange?: (values: UpdateEventToFitnessTypeDto[]) => void;
};

export const ActivityAutocomplete: FC<ActivityAutocompleteProps> = props => {
	const { options, value = [], onChange, allowSelectMasteryLevel = false, ...restProps } = props;

	const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);

	const valueOptions = value
		?.filter(fitness => !fitness.delete)
		.map(value => options.find(option => option?.value === value.fitnessTypeId))
		.filter(x => !!x);

	const removeOption = useCallback(
		(newOptions: Option[]) => {
			// find the deleted value which is not in the new values
			const deletedFitnessId = findRemovedElement(value, newOptions);

			// mark this value as deleted for backend
			const newValues = value.map(oldValue =>
				oldValue.fitnessTypeId === deletedFitnessId ? { ...oldValue, delete: true } : oldValue
			);

			onChange?.(newValues);
		},
		[value, onChange]
	);

	const selectActivityWithoutMasteryLevel = useCallback(
		(activityId: string) => {
			if (!activityId) return;

			const newValues = [
				...getOldActivityValues([activityId], value),
				{ fitnessTypeId: activityId, masteryLevel: 0 },
			] as UpdateEventToFitnessTypeDto[];

			onChange?.(newValues);
		},
		[value, onChange]
	);

	const selectActivityWithMasterLevel = useCallback(
		(masteryLevel: number) => {
			if (!selectedActivityId) return;

			const activity = {
				fitnessTypeId: selectedActivityId,
				masteryLevel,
			} as UpdateEventToFitnessTypeDto;

			const existingValue = value.find(value => value.fitnessTypeId === activity.fitnessTypeId && !value.delete);

			// if the activity already exists, update the mastery level
			const newValues = existingValue
				? [
						...value.map(val => ({
							...val,
							masteryLevel: val.fitnessTypeId === activity.fitnessTypeId ? activity.masteryLevel : val.masteryLevel,
						})),
				  ]
				: // if the activity does not exist, add it
				  [...getOldActivityValues([activity.fitnessTypeId], value), activity];

			onChange?.(newValues);

			setSelectedActivityId(null);
		},
		[selectedActivityId, value, onChange]
	);

	const selectOption = useCallback(
		(newOptions: Option[]) => {
			// find the new value which is not in the old values (may reselect previously deleted)
			const newFitnessId = findNewElement(value, newOptions);

			if (!newFitnessId) return;

			if (allowSelectMasteryLevel) {
				setSelectedActivityId(newFitnessId);
			} else {
				selectActivityWithoutMasteryLevel(newFitnessId);
			}
		},
		[value, allowSelectMasteryLevel, selectActivityWithoutMasteryLevel]
	);

	return (
		<Autocomplete
			{...restProps}
			value={valueOptions}
			options={options}
			multiple
			getOptionLabel={option => (option as Option)?.label || ''}
			getOptionKey={option => (option as Option)?.value || ''}
			disableCloseOnSelect
			// do not set onBlur to prevent unintended closing of the popper
			overrideRenderOption={(props, option, state) => {
				const { selected } = state;
				const { label, value, icon } = option as Option;

				return (
					<MenuItem {...props} key={value} component='li' selected={selected} sx={autocompleteOptionStyle}>
						{icon}
						<Typography
							sx={{
								color: Colors.white,
							}}
						>
							{label}
						</Typography>
					</MenuItem>
				);
			}}
			onChange={(_event, values, reason) => {
				const newOptions = values as Option[];

				if (reason === 'clear' || !values) {
					onChange?.([]);
				} else if (reason === 'removeOption' && Array.isArray(values)) {
					removeOption(newOptions);
				} else if (reason === 'selectOption' && Array.isArray(values)) {
					selectOption(newOptions);
				}
			}}
			PopperComponent={
				selectedActivityId
					? (props: PopperProps) => {
							const option = options.find(option => option?.value === selectedActivityId);

							return (
								<Popper
									{...props}
									open={!!selectedActivityId}
									onClick={e => {
										e.stopPropagation();
									}}
									children={
										<Box
											onClick={e => {
												e.stopPropagation();
											}}
											sx={masteryLevelContainerStyle}
										>
											<Box sx={masteryLevelContainerHeader}>
												<IconButton
													onClick={() => {
														setSelectedActivityId(null);
													}}
												>
													<ArrowBackIcon />
												</IconButton>

												<Box sx={masteryLevelSelectedActivityStyle}>
													{option?.icon}
													<Typography sx={masteryLevelSelectedActivityLabelStyle}>{option?.label}</Typography>
												</Box>

												<Typography sx={masteryLevelSelectLabel}>Select level:</Typography>
											</Box>
											<Box sx={masteryLevelButtonsContainer}>
												{Array.from({ length: 4 })
													.fill(null)
													.map((_, masteryLevel) => (
														<Box
															key={masteryLevel}
															onClick={e => {
																e.stopPropagation();
																selectActivityWithMasterLevel(masteryLevel);
															}}
															sx={masteryLevelButton}
														>
															<Box sx={masteryLevelIconWrapper}>
																{Array.from({ length: 3 })
																	.fill(null)
																	.map((_, index) => (
																		<Box key={index} sx={masteryLevelIconLine(index < masteryLevel)} />
																	))}
															</Box>
															<Typography sx={masteryLevelLabel}>
																{masteryLevel === 0
																	? 'All levels'
																	: masteryLevel === 1
																	? 'Beginner'
																	: masteryLevel === 2
																	? 'Intermediate'
																	: 'Advanced'}
															</Typography>
														</Box>
													))}
											</Box>
										</Box>
									}
									placement='bottom-end'
									style={{
										...(props.style ?? {}),
										minWidth: '280px',
									}}
								/>
							);
					  }
					: (props: PopperProps) => (
							<Popper
								{...props}
								placement='bottom-end'
								style={{
									...(props.style ?? {}),
									minWidth: '280px',
								}}
							/>
					  )
			}
			renderTags={(tagValues, getTagProps) =>
				(tagValues as Option[])?.map((option, index) => {
					const { onDelete, ...props } = getTagProps({ index });

					const activity = value.find(act => act.fitnessTypeId === option?.value);

					return (
						<CoreAutocompleteChip
							{...props}
							key={option?.value}
							onClick={() => {
								if (allowSelectMasteryLevel) {
									setSelectedActivityId(option?.value);
								}
							}}
							onDelete={onDelete}
							icon={
								<span
									className='MuiChip-icon'
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									{option?.icon}
								</span>
							}
							label={
								<Box sx={tagLabelBox}>
									<Typography sx={tagLabel}>{option?.label}</Typography>
									{allowSelectMasteryLevel && (
										<Typography sx={tagMasteryLevelLabel}>
											:{' '}
											{!activity || activity?.masteryLevel === 0
												? 'All levels'
												: activity?.masteryLevel === 1
												? 'Beginner'
												: activity?.masteryLevel === 2
												? 'Intermediate'
												: 'Advanced'}
										</Typography>
									)}
								</Box>
							}
						/>
					);
				})
			}
		/>
	);
};
